/** First-run onboarding state, stored in the `onboarding` user preference as JSON { step, completedAt } */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { ProjectSummary } from '@agentwiki/shared'
import { apiClient } from '../lib/api-client'
import { useAuth } from './use-auth'
import { usePreferences } from './use-preferences'
import { useProjects } from './use-projects'

export type OnboardingStep = 'completed' | 'skipped'

export interface OnboardingState {
  step: OnboardingStep
  completedAt: string
}

export function parseOnboarding(raw: string | undefined): OnboardingState | null {
  if (!raw) return null
  try {
    const value = JSON.parse(raw) as Partial<OnboardingState>
    return value.step === 'completed' || value.step === 'skipped' ? (value as OnboardingState) : null
  } catch {
    return null
  }
}

/**
 * Onboarding is shown once, to workspace admins who have not finished or skipped it and whose workspace has
 * no project besides the default one. People already organising work into projects are not interrupted, and
 * other members are not asked to rename a project they share (they may not be allowed to).
 */
export function needsOnboarding(prefs: Record<string, string>, projects: ProjectSummary[], role: string | null): boolean {
  return role === 'admin' && parseOnboarding(prefs.onboarding) === null && projects.length <= 1
}

/** 'loading' until both preferences and projects are known, so the gate never flashes the shell first */
export function useOnboardingStatus(): 'loading' | 'needed' | 'done' {
  const prefs = usePreferences()
  const projects = useProjects()
  const { role } = useAuth()
  // A failed lookup must not lock the user out of the app
  if (prefs.isError || projects.isError) return 'done'
  if (!prefs.data || !projects.data) return 'loading'
  return needsOnboarding(prefs.data, projects.data, role) ? 'needed' : 'done'
}

export function useFinishOnboarding() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (step: OnboardingStep) => {
      const value = JSON.stringify({ step, completedAt: new Date().toISOString() } satisfies OnboardingState)
      try {
        await apiClient.put<{ ok: boolean }>('/api/preferences/onboarding', { value })
      } catch (err) {
        // Never trap the user on /onboarding: let them in for this session; it shows again on the next load
        console.error('Could not save onboarding state:', err)
      }
      return value
    },
    // Update the cache before navigating so the gate does not send the user back to /onboarding
    onSuccess: (value) => qc.setQueryData<Record<string, string>>(['preferences'], (old) => ({ ...old, onboarding: value })),
  })
}
